import { useI18n } from '@/hooks/useI18n';
import { useMemo } from 'react';
import { ExternalLink, Eye, Heart } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import type { ReelResponse } from '@/types/api';
import { buildReelUrl } from '@/lib/ig';
import { formatDate, formatNumber } from '@/lib/format';
import { proxyImage } from '@/lib/imageProxy';

interface ReelLightboxProps {
  reel: ReelResponse | null;
  allReels: ReelResponse[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ReelLightbox({ reel, allReels, open, onOpenChange }: ReelLightboxProps) {
  const { t } = useI18n();
  const percentile = useMemo(() => {
    if (!reel) return null;
    const sorted = allReels.map((r) => r.viewsCount).sort((a, b) => a - b);
    if (sorted.length === 0) return null;
    const below = sorted.filter((v) => v < reel.viewsCount).length;
    return below / sorted.length;
  }, [reel, allReels]);

  if (!reel) return null;
  const viral = !!reel.viralFlaggedAt;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl border-line-1 bg-bg-1 p-0">
        <DialogTitle className="sr-only">Reel {reel.shortcode}</DialogTitle>
        <div className="grid gap-0 md:grid-cols-[1fr_320px]">
          <div className="relative h-[min(48dvh,420px)] w-full overflow-hidden bg-black md:h-[560px]">
            {reel.thumbnailUrl ? (
              <img
                src={proxyImage(reel.thumbnailUrl)}
                alt=""
                className="h-full w-full object-contain"
              />
            ) : (
              <iframe
                src={`${buildReelUrl(reel.shortcode)}embed`}
                title={reel.shortcode}
                className="h-full w-full"
                allowFullScreen
                loading="lazy"
              />
            )}
          </div>

          <div className="flex min-w-0 flex-col gap-4 p-4 sm:p-5 md:border-l md:border-line-1">
            <div>
              <div className="break-all text-[11px] font-semibold uppercase tracking-wider text-text-3">
                {reel.shortcode}
              </div>
              <div className="mt-0.5 text-[12px] text-text-3">{formatDate(reel.publishedAt)}</div>
              {viral && (
                <div className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-violet-soft px-2 py-0.5 text-[11px] font-semibold text-violet-deep">
                  <span className="h-1.5 w-1.5 rounded-full bg-violet" />
                  Viral
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <Metric icon={Eye} label={t('Vues', 'Views')} value={reel.viewsCount} />
              <Metric icon={Heart} label={t('J’aime', 'Likes')} value={reel.likesCount} />
            </div>

            {percentile !== null && (
              <div className="rounded-[12px] border border-violet/20 bg-violet-soft p-3">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-text-3">
                  {t('Rang', 'Rank')}
                </div>
                <div className="font-display mt-1 text-[22px] text-violet-deep">
                  top {Math.max(1, Math.round((1 - percentile) * 100))}%
                </div>
                <div className="text-[11px] text-text-3">
                  {t('des reels de ce compte', 'of this account’s reels')}
                </div>
              </div>
            )}

            {reel.caption && (
              <div className="space-y-1">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-text-3">
                  {t('Légende', 'Caption')}
                </div>
                <p className="max-h-[200px] overflow-y-auto whitespace-pre-line break-words text-[13px] text-text-2">
                  {reel.caption}
                </p>
              </div>
            )}

            <Button asChild variant="secondary" size="sm" className="gap-1.5">
              <a href={buildReelUrl(reel.shortcode)} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="h-3.5 w-3.5" />
                {t('Ouvrir sur Instagram', 'Open on Instagram')}
              </a>
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Metric({
  icon: Icon,
  label,
  value,
}: {
  icon: typeof Eye;
  label: string;
  value: number | null;
}) {
  useI18n();
  return (
    <div className="rounded-[12px] border border-line-1 bg-bg-0/60 p-3">
      <div className="flex items-center gap-1 text-[11px] font-medium text-text-3">
        <Icon className="h-3 w-3" />
        {label}
      </div>
      <div className="font-display mt-0.5 text-[22px] tabular-nums text-text-1">
        {value !== null ? formatNumber(value) : '—'}
      </div>
    </div>
  );
}
